/**
 * User Data Service for Firebase Realtime Database
 * 
 * Handles saving and retrieving user profile data from Firebase Realtime Database.
 */

import { db } from './firebase';
import { ref, set, get } from 'firebase/database';
import { syncPublicProfile } from '../services/publicProfileService';

// Sensitive fields must be managed by trusted backend flows / admin tooling only.
const RESTRICTED_PROFILE_FIELDS = new Set([
  "role",
  "roles",
  "subscriptions",
  "followerCount",
]);

const sanitizeProfileUpdates = (updates = {}) =>
  Object.fromEntries(
    Object.entries(updates).filter(([key]) => !RESTRICTED_PROFILE_FIELDS.has(key))
  );

/**
 * Save user profile data to Firebase Realtime Database
 * @param {string} userId - Firebase user UID
 * @param {Object} userData - User data to save (phoneNumber, gender, etc.)
 * @returns {Promise<void>}
 */
export const saveUserData = async (userId, userData) => {
  try {
    const userRef = ref(db, `users/${userId}`);
    const next = {
      ...userData,
      updatedAt: new Date().toISOString(),
    };
    await set(userRef, next);
    await syncPublicProfile(userId, next);
  } catch (error) {
    console.error('Error saving user data:', error);
    throw error;
  }
};

/**
 * Get user profile data from Firebase Realtime Database
 * @param {string} userId - Firebase user UID
 * @returns {Promise<Object|null>} User data or null if not found
 */
export const getUserData = async (userId) => {
  try {
    const userRef = ref(db, `users/${userId}`);
    const snapshot = await get(userRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

/**
 * Update user profile data in Firebase Realtime Database
 * @param {string} userId - Firebase user UID
 * @param {Object} updates - Partial user data to update
 * @returns {Promise<void>}
 */
export const updateUserData = async (userId, updates) => {
  try {
    const safeUpdates = sanitizeProfileUpdates(updates);
    const userRef = ref(db, `users/${userId}`);
    const snapshot = await get(userRef);
    
    if (snapshot.exists()) {
      const currentData = snapshot.val();
      const next = {
        ...currentData,
        ...safeUpdates,
        updatedAt: new Date().toISOString(),
      };
      await set(userRef, next);
      await syncPublicProfile(userId, next);
    } else {
      const next = {
        ...safeUpdates,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await set(userRef, next);
      await syncPublicProfile(userId, next);
    }
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

